import { useEffect } from "react";

/**
 * Universal Native Reticle Cursor
 * - Replaces OS cursor with a ring+dot (and a larger hover version).
 * - Works in browsers and Android WebViews (Vuplex) because it's pure CSS cursor.
 * - No props, no overlays, no pointer listeners.
 */
export default function ReticleCursor() {
  useEffect(() => {
    const makeCursor = ({ size, ringColor, coreColor, ringWidth, coreRadius }) => {
      const c = document.createElement("canvas");
      c.width = c.height = size;
      const ctx = c.getContext("2d");
      const cx = size / 2, cy = size / 2;

      // outer ring
      ctx.beginPath();
      ctx.arc(cx, cy, (size / 2) - ringWidth, 0, Math.PI * 2);
      ctx.strokeStyle = ringColor;
      ctx.lineWidth = ringWidth;
      ctx.stroke();

      // inner core (hollow dot)
      ctx.beginPath();
      ctx.arc(cx, cy, coreRadius, 0, Math.PI * 2);
      ctx.strokeStyle = coreColor;
      ctx.lineWidth = ringWidth;
      ctx.stroke();

      return c.toDataURL("image/png");
    };

    // Normal + hover variants (same style as your image)
    const normalSize = 24, hoverSize = 28;
    const ringColor  = "#15ff00";
    const coreColor  = "#3cb1ffff";
    const hoverCore  = "#3cb1ffff";
    const ringWidth  = 2;
    const coreR      = 1;
    const hoverCoreR = 7;

    const normalUrl = makeCursor({ size: normalSize, ringColor, coreColor, ringWidth, coreRadius: coreR });
    const hoverUrl  = makeCursor({ size: hoverSize,  ringColor, coreColor: hoverCore, ringWidth, coreRadius: hoverCoreR });

    const root = document.documentElement;
    root.style.setProperty("--cursor-reticle", `url("${normalUrl}")`);
    root.style.setProperty("--cursor-reticle-hover", `url("${hoverUrl}")`);

    // Hotspots at the center of each image
    const normalHotspot = `${Math.round(normalSize / 2)} ${Math.round(normalSize / 2)}`;
    const hoverHotspot  = `${Math.round(hoverSize  / 2)} ${Math.round(hoverSize  / 2)}`;

    // Inject CSS once (create or update)
    const STYLE_ID = "reticle-native-style";
    const css = `
      /* Base: show the reticle cursor globally */
      html {
        cursor: var(--cursor-reticle) ${normalHotspot}, auto;
      }

      /* Clickable targets: slightly larger + green core */
      button,
      a[href],
      [role="button"],
      .hover-target {
        cursor: var(--cursor-reticle-hover) ${hoverHotspot}, pointer;
      }

      /* Optional: keep text caret on inputs/textareas/contenteditable */
      input, textarea, [contenteditable="true"] {
        cursor: text;
      }
      select { cursor: default; }
    `.trim();

    let styleEl = document.getElementById(STYLE_ID);
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = STYLE_ID;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = css;

    // Cleanup not required; we keep the cursor for the app lifetime.
    // Return a noop to satisfy React.
    return () => {};
  }, []);

  return null; // no DOM rendered
}
